/**
 * Notifications settings form defaults.
 */
import type { z } from "zod";
import type { notifications } from "@flint/api-contract";

type PreferencesRow = z.infer<typeof notifications.getPreferencesOutput>;
type PreferencesUpdate = z.infer<typeof notifications.updatePreferencesInput>;
export type NotificationEventValues = z.infer<typeof notifications.eventPreference>;

export interface NotificationsFormValues {
  notificationsEnabled: boolean;
  events: NotificationEventValues[];
}

export const EMPTY_NOTIFICATIONS_FORM: NotificationsFormValues = {
  notificationsEnabled: true,
  events: [],
};

export function formFromPreferences(c: PreferencesRow): NotificationsFormValues {
  return { notificationsEnabled: c.masterEnabled, events: c.events };
}

/** Build the wire patch from a diff — null when nothing changed. */
export function valuesToUpdatePayload(
  v: NotificationsFormValues,
  base: NotificationsFormValues,
): PreferencesUpdate | null {
  const payload: PreferencesUpdate = {};
  if (v.notificationsEnabled !== base.notificationsEnabled) {
    payload.masterEnabled = v.notificationsEnabled;
  }
  const changedEvents = v.events.filter((event) => {
    const current = base.events.find((b) => b.eventKey === event.eventKey);
    return !current || JSON.stringify(stripMeta(event)) !== JSON.stringify(stripMeta(current));
  });
  if (changedEvents.length > 0) {
    payload.events = changedEvents.map(stripMeta);
  }
  return payload.masterEnabled !== undefined || payload.events ? payload : null;
}

export function isDirtyVs(v: NotificationsFormValues, base: NotificationsFormValues): boolean {
  return valuesToUpdatePayload(v, base) !== null;
}

function stripMeta(event: NotificationEventValues): NonNullable<PreferencesUpdate["events"]>[number] {
  return {
    eventKey: event.eventKey,
    enabled: event.enabled,
    whatsappEnabled: event.whatsappEnabled,
    emailEnabled: event.emailEnabled,
    smsEnabled: event.smsEnabled,
    primaryChannel: event.primaryChannel,
    fallbackChannel: event.fallbackChannel,
    firstReminderAfterDays: event.firstReminderAfterDays,
    repeatEveryDays: event.repeatEveryDays,
    maxReminderCount: event.maxReminderCount,
  };
}
